import { useMemo } from 'react';
import backend from '../services/backend';
import { useParams, useSearchParams } from 'react-router-dom';
import { TableSkeleton } from '../components/Skeleton';
import { entityViewTitle } from '../components/ID';
import { useQuery } from '@tanstack/react-query';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Tabs, type TabDef } from '../components/Tabs';
import { Header } from './projectView/Header';
import { ProjectDetails } from './projectView/ProjectDetails';
import { EngineEvents } from './projectView/EngineEvents';
import { LLPStatus } from './projectView/LLPStatus';
import { LLPEvents } from './projectView/LLPEvents';
import { SourceCard } from './projectView/SourceCard';
import { LLPStatusWarnings } from './projectView/LLPStatusWarnings';
import { ADEvents } from './projectView/ADEvents';
import { SBEvents } from './projectView/SBEvents';
import { LDNDEvents } from './projectView/LDNDEvents';
import { QecListEvents } from './projectView/QecListEvents';
import { BillOfSaleEvents } from './projectView/BillOfSaleEvents';
import { UtilisationSummary } from './projectView/UtilisationSummary';
import { WorkscopeEvents } from './projectView/WorkscopeEvents';
import { NisEvents } from './projectView/NisEvents';
import { ThrustConversionEvents } from './projectView/ThrustConversionEvents';
import { PmaDerEvents } from './projectView/PmaDerEvents';
import { WebhookDeliveries } from './projectView/WebhookDeliveries';
import { CorrectionsTab } from './projectView/CorrectionsTab';
import { TabEmptyState } from './projectView/TabEmptyState';
import { DocumentTypeCards } from './projectView/DocumentTypeCards';
import { ProjectOverviewSummaries } from './projectView/ProjectOverviewSummaries';
import { PeekHost } from '../components/PeekHost';
import { ProjectAgentDrawer } from '../components/agent/ProjectAgentDrawer';

const TAB_KEYS = [
  'overview',
  'engine',
  'llp',
  'ad',
  'sb',
  'ldnd',
  'qec',
  'bos',
  'utilisation',
  'workscope',
  'nis',
  'thrust',
  'pma-der',
  'webhooks',
  'corrections',
] as const;
type TabKey = (typeof TAB_KEYS)[number];

export default function ProjectView() {
  const { id } = useParams<{ id: string }>();
  useDocumentTitle(entityViewTitle(id, 'Project View'));
  const [searchParams, setSearchParams] = useSearchParams();

  const projectQuery = useQuery({
    queryKey: ['getProject', id],
    queryFn: () => backend.getProject(id!).then((res) => res.data),
    enabled: !!id,
  });

  const loading = projectQuery.isLoading;
  const error = projectQuery.error as Error | null;
  const response = projectQuery.data;

  const documentIds = useMemo(() => {
    return response?.item.documents.map((document) => document.documentId);
  }, [response]);

  const rawTab = searchParams.get('tab');
  const activeTab: TabKey = TAB_KEYS.includes(rawTab as TabKey) ? (rawTab as TabKey) : 'overview';

  const setActiveTab = (key: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'overview') {
          next.delete('tab');
        } else {
          next.set('tab', key);
        }
        return next;
      },
      { replace: false },
    );
  };

  if (loading && !response) {
    return (
      <div className="">
        <Header />
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="">
        <Header />
        <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 shadow dark:shadow-zinc-950/30 rounded-lg p-6">
          <p className="text-center text-red-600 dark:text-red-400">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!response) {
    return null;
  }

  const data = response.item.data;
  const tabs: TabDef[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'engine', label: 'Engine Events', count: data?.engineEvents?.engineEvents?.length ?? 0 },
    { key: 'llp', label: 'LLP', count: data?.llpEvents?.parts?.length ?? 0 },
    { key: 'ad', label: 'AD Status', count: data?.adEvents?.ads?.length ?? 0 },
    { key: 'sb', label: 'SB Status', count: data?.sbEvents?.sbs?.length ?? 0 },
    { key: 'ldnd', label: 'LDND', count: data?.ldndEvents?.ldndStatusEvents?.length ?? 0 },
    { key: 'qec', label: 'QEC / Accessory', count: data?.qecEvents?.qecListEvents?.length ?? 0 },
    { key: 'bos', label: 'Bill of Sale', count: data?.bosEvents?.billOfSaleEvents?.length ?? 0 },
    { key: 'utilisation', label: 'Utilisation' },
    {
      key: 'workscope',
      label: 'Engine Workscope',
      count: data?.workscopeEvents?.workscopeEvents?.length ?? 0,
    },
    { key: 'nis', label: 'NIS Statement', count: data?.nisEvents?.nisEvents?.length ?? 0 },
    {
      key: 'thrust',
      label: 'Thrust Change',
      count: data?.thrustConversionEvents?.thrustConversionEvents?.length ?? 0,
    },
    {
      key: 'pma-der',
      label: 'PMA/DER',
      count: data?.pmaDerEvents?.pmaDerEvents?.length ?? 0,
    },
    { key: 'webhooks', label: 'Webhook Deliveries' },
    { key: 'corrections', label: 'Corrections', count: response.item.supervisorRun?.findings },
  ];

  return (
    <div className="">
      <PeekHost />
      {/* Keyed by project so one project's chat history never reaches another project's route. */}
      <ProjectAgentDrawer key={id} projectId={id!} />
      <Header
        documentIds={documentIds}
        debugObject={response.item}
        extractionFreshness={response.item.extractionFreshness}
      />

      <div className="flex flex-col gap-6">
        <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-8">
            <ProjectDetails response={response} />
            <div className="flex flex-col gap-8">
              <ProjectOverviewSummaries response={response} />
              <DocumentTypeCards projectId={id!} />
            </div>
          </div>
        )}

        {activeTab === 'engine' &&
          (data?.engineEvents ? (
            <EngineEvents response={response} />
          ) : (
            <TabEmptyState label="engine events" />
          ))}

        {activeTab === 'llp' &&
          (data?.llpEvents?.parts ? (
            <div className="space-y-6">
              <SourceCard
                sources={[
                  data.llpEvents.nis && {
                    label: 'NIS',
                    esn: data.llpEvents.nis.esn,
                    documentId: data.llpEvents.nis.documentId,
                    pageIds: data.llpEvents.nis.pageIds,
                  },
                  data.llpEvents.llp && {
                    label: 'LLP',
                    esn: data.llpEvents.llp.esn,
                    documentId: data.llpEvents.llp.documentId,
                    pageIds: data.llpEvents.llp.pageIds,
                  },
                ]}
              >
                <LLPStatusWarnings warnings={data.llpEvents.warnings} />
              </SourceCard>
              <LLPStatus response={response} />
              <LLPEvents response={response} />
            </div>
          ) : (
            <TabEmptyState label="LLP data" />
          ))}

        {activeTab === 'ad' &&
          (data?.adEvents?.ads ? (
            <ADEvents key={response.item.id} response={response} />
          ) : (
            <TabEmptyState label="AD status events" />
          ))}

        {activeTab === 'sb' &&
          (data?.sbEvents?.sbs ? (
            <SBEvents key={`sb-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="SB status events" />
          ))}

        {activeTab === 'ldnd' &&
          (data?.ldndEvents?.ldndStatusEvents ? (
            <LDNDEvents key={`ldnd-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="LDND events" />
          ))}

        {activeTab === 'qec' &&
          (data?.qecEvents?.qecListEvents ? (
            <QecListEvents key={`qec-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="QEC / accessory list" />
          ))}

        {activeTab === 'bos' &&
          (data?.bosEvents?.billOfSaleEvents?.length ? (
            <BillOfSaleEvents key={`bos-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="bill of sale events" />
          ))}

        {activeTab === 'utilisation' &&
          (data?.utilisation ? (
            <UtilisationSummary key={`util-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="utilisation data" />
          ))}

        {activeTab === 'workscope' &&
          (data?.workscopeEvents?.workscopeEvents?.length ? (
            <WorkscopeEvents key={`workscope-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="engine workscope events" />
          ))}

        {activeTab === 'nis' &&
          (data?.nisEvents?.nisEvents?.length ? (
            <NisEvents key={`nis-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="NIS statements" />
          ))}

        {activeTab === 'thrust' &&
          (data?.thrustConversionEvents?.thrustConversionEvents?.length ? (
            <ThrustConversionEvents key={`thrust-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="thrust conversion events" />
          ))}

        {activeTab === 'pma-der' &&
          (data?.pmaDerEvents?.pmaDerEvents?.length ? (
            <PmaDerEvents key={`pma-der-${response.item.id}`} response={response} />
          ) : (
            <TabEmptyState label="PMA/DER statements" />
          ))}

        {activeTab === 'webhooks' && <WebhookDeliveries projectId={id!} />}

        {activeTab === 'corrections' && <CorrectionsTab projectId={id!} />}
      </div>
    </div>
  );
}
